import { useCallback, useEffect, useRef } from 'react';
import useUserActivityStore from 'src/store/user-activity';

type UseIdleTrackerOptions = {
  idleTimeout?: number;
  events?: string[];
};

const DEFAULT_IDLE_TIMEOUT = 2 * 60_000; // 2 mins
const DEFAULT_IDLE_CHECK_TIMEOUT = 20_000; // 20 secs

/**
 * returns a boolean indicating if the user is idle or not.
 */
export function useIdleTracker({
  idleTimeout = DEFAULT_IDLE_TIMEOUT,
  events = ['mousedown', 'keydown', 'touchstart', 'scroll'],
}: UseIdleTrackerOptions = {}) {
  const { isIdle, setIsIdle } = useUserActivityStore();

  const lastActiveRef = useRef<number>(Date.now());

  const updateActivity = useCallback(() => {
    setIsIdle(false);
    lastActiveRef.current = Date.now();
  }, [setIsIdle]);

  useEffect(() => {
    events.forEach((eventName) => {
      document.addEventListener(eventName, updateActivity, true);
    });

    const checkIdle = () => {
      const now = Date.now();

      const elapsed = now - lastActiveRef.current;
      const anyInputHasValue = checkAnyTextInputsPopulated();

      if (elapsed > idleTimeout && !anyInputHasValue) {
        setIsIdle(true);
      }
    };

    const intervalId = window.setInterval(checkIdle, DEFAULT_IDLE_CHECK_TIMEOUT);

    return () => {
      events.forEach((eventName) => {
        document.removeEventListener(eventName, updateActivity, true);
      });
      clearInterval(intervalId);
    };
  }, [setIsIdle, idleTimeout, events, updateActivity]);

  return { isIdle };
}

/**
 * Scans all text/textarea fields in the DOM
 * Returns true if any input has a non-empty value.
 */
function checkAnyTextInputsPopulated(): boolean {
  const inputs = document.querySelectorAll('input[type="text"], textarea');

  for (const el of inputs) {
    const value = (el as HTMLInputElement)?.value || '';

    if (value.trim() !== '') {
      return true;
    }
  }

  return false;
}
